import React, { useState } from "react";
import { Facebook, MessageCircle, Link2, Check } from "lucide-react";

/**
 * ShareRow
 *
 * Compact one-line share strip for public Q&A pages. Boosts organic reach
 * because every renter who finds a useful answer can spread it in two
 * clicks. Designed to be unobtrusive, no big "SHARE THIS!" banner.
 *
 * Includes Facebook, X (Twitter), WhatsApp and Copy-link. Each click
 * fires a best-effort beacon to /api/share-clicks so the admin can see
 * which Q&As are spreading and through which channel.
 *
 * Props:
 *   url:   string , the URL to share (defaults to window.location.href)
 *   title: string , used as share text where supported
 *   slug:  string , Q&A slug, recorded with the beacon for analytics
 */
export default function ShareRow({ url, title, slug }) {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
  const shareText = title ? `${title}, TenantTrail` : "TenantTrail";

  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(shareText);

  // Best-effort analytics ping. Wrapped in try/catch so a network blip
  // never throws or blocks the user's actual share action.
  const beacon = (channel) => {
    try {
      const backend = process.env.REACT_APP_BACKEND_URL;
      if (!backend) return;
      const body = JSON.stringify({
        channel,
        url: shareUrl,
        slug: slug || null,
        title: title || null,
        referrer: typeof document !== "undefined" ? document.referrer : null,
        user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      // keepalive=true lets the request survive the page navigating away
      fetch(`${backend}/api/share-clicks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body,
        keepalive: true,
      }).catch(() => { /* ignored */ });
    } catch {
      /* ignored */
    }
  };

  const targets = [
    {
      key: "facebook",
      label: "Share on Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
    },
    {
      key: "x",
      label: "Share on X",
      href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedText}`,
      // X / Twitter has no lucide brand icon since rebrand; use a tiny inline SVG.
      Icon: XIcon,
    },
    {
      key: "whatsapp",
      label: "Share on WhatsApp",
      href: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      Icon: MessageCircle,
    },
  ];

  const onCopy = async () => {
    beacon("copy");
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(shareUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      /* clipboard may be blocked in iframe / non-https, silently no-op */
    }
  };

  return (
    <div
      className="flex items-center gap-1.5 flex-wrap mb-6 text-xs"
      data-testid="share-row"
    >
      <span className="label-mono text-[#9CA3AF] mr-1">Share</span>
      {targets.map(({ key, label, href, Icon }) => (
        <a
          key={key}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          onClick={() => beacon(key)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E4DE] bg-white text-[#5a6b7a] hover:text-[#1B6C62] hover:border-[#1B6C62]/40 hover:bg-[#E6F5F2]/50 transition-colors"
          data-testid={`share-${key}`}
        >
          <Icon size={12} strokeWidth={1.75} />
          <span className="font-mono text-[10px] uppercase tracking-[0.1em]">{key === "x" ? "X" : key}</span>
        </a>
      ))}
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy link"
        title="Copy link"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 border border-[#E5E4DE] bg-white text-[#5a6b7a] hover:text-[#1B6C62] hover:border-[#1B6C62]/40 hover:bg-[#E6F5F2]/50 transition-colors"
        data-testid="share-copy"
      >
        {copied ? <Check size={12} strokeWidth={2} className="text-[#1B6C62]" /> : <Link2 size={12} strokeWidth={1.75} />}
        <span className="font-mono text-[10px] uppercase tracking-[0.1em]">
          {copied ? "Copied" : "Copy"}
        </span>
      </button>
    </div>
  );
}

// Tiny X-logo SVG so the brand recolours via currentColor like the rest.
function XIcon({ size = 12, strokeWidth = 1.75 }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ strokeWidth }}
    >
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}
